import { useEffect, useMemo, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import Sheet from "../Sheet.jsx";
import TraceTable from "../TraceTable.jsx";
import { CompositionPanel, COMPOSITION_ROWS } from "./Composition.jsx";
import { useQuery } from "../../lib/dataClient.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { groupByName } from "../skills/grouping.js";
import { toolFingerprint } from "../../lib/toolDefs.js";
import { extractServerFromToolName, shortToolName, labelForMcpServer } from "../../lib/mcpServers.js";

const TRACE_LIMIT = 200;

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
        active
          ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
          : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

// Per-tool VALUE lens, scoped to one tool — the single-tool sibling of
// McpServerDetailBody (an MCP server is just this rolled up over its mcp__ tools):
//   • DEFINITION + drift — READ from the durable `tool` / `tool.fingerprint`
//     signal_tags (the backend `tool` signal materializes each offered tool's
//     declared {name, description, schema} + a drift fingerprint), grouped into
//     variants (>1 = drift: version skew or tampering). Fleet-complete + instant,
//     covering built-in AND MCP tools — not a per-view browser sample.
//   • CLUSTER composition — who calls it, with a concentration verdict.
//   • RECENT calls — the scoped prompts that called this tool.
// For an MCP tool (mcp__<server>__<tool>) the hero links up to its server.
export default function ToolDetailBody({ nav, slot, ctx }) {
  const scopePath = nav?.scopePath || [];
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const toolName = terminal?.dim === "tools" ? (terminal.ids?.[0] ?? terminal.id ?? null) : null;
  const server = toolName ? extractServerFromToolName(toolName) : null;
  const heroTitle = toolName ? (server ? shortToolName(toolName) : toolName) : "";
  const range = ctx?.range;

  // ── Definition + drift — the `tool` signal_tags value is JSON
  // {name, description, schema}. Compute the drift key from each def with
  // toolFingerprint (the same key the `tool.fingerprint` facet carries) and group
  // by it: distinct fingerprints ARE the definition variants (this cross-event
  // reduction is what the per-event signal can't do). One query — the key comes
  // from the def itself, so there's no second fingerprint fetch to misalign with.
  // The tag (tool name) selects the prompts that offered the tool.
  const { data: defData, loading: defLoading } = useQuery(
    "getSignalTagValues",
    { namespace: "tool", tag: toolName, scopePath, range },
    { enabled: Boolean(toolName) },
  );
  const found = useMemo(() => {
    const out = [];
    for (const r of defData?.rows ?? []) {
      let d;
      try { d = JSON.parse(r.value); } catch { continue; }
      if (!d || typeof d !== "object") continue;
      out.push({ eventId: r.event_id, def: d, fingerprint: toolFingerprint(d) });
    }
    return out;
  }, [defData]);

  const versions = useMemo(
    () => groupByName(found, (r) => r.fingerprint)
      .map((g) => ({ count: g.rows.length, def: g.rows[0].def }))
      .sort((a, b) => b.count - a.count)
      .map((v, i) => ({ ...v, n: i + 1 })),
    [found],
  );
  const [versionSel, setVersionSel] = useState(null);
  // Selecting a variant by number; reset it when the tool changes so a stale
  // v-index from a prior tool can't carry onto an unrelated definition.
  useEffect(() => { setVersionSel(null); }, [toolName]);
  const activeVersion = versions.find((v) => v.n === versionSel) || versions[0] || null;
  const activeDef = activeVersion?.def || null;

  // ── Recent calls + scope total — prompts that CALLED this tool (the tools
  // scope), server-paginated. No client window scrubber: the definition is read
  // from signal_tags above, not scanned over a window (mirrors McpServerDetailBody).
  const { data: traces = [], loading: tracesLoading } = useQuery(
    "getTraces",
    { scopePath, range, limit: TRACE_LIMIT, sort: "newest" },
    { enabled: Boolean(toolName) },
  );
  const scopeTotal = Number(traces?.total) || traces.length || 0;

  // ── Composition — who calls this tool.
  const compEnabled = Boolean(toolName);
  const { data: epData, loading: epLoading } = useQuery("getDimensionEntries", {
    dimKey: "endpoints", parentScopePath: scopePath, limit: COMPOSITION_ROWS, range,
  }, { enabled: compEnabled });
  const { data: idData, loading: idLoading } = useQuery("getDimensionEntries", {
    dimKey: "identities", parentScopePath: scopePath, limit: COMPOSITION_ROWS, range,
  }, { enabled: compEnabled });
  const { data: agData, loading: agLoading } = useQuery("getDimensionEntries", {
    dimKey: "agents", parentScopePath: scopePath, limit: COMPOSITION_ROWS, range,
  }, { enabled: compEnabled });

  const concentration = useMemo(() => {
    const top = epData?.entries?.[0];
    const distinct = epData?.total ?? 0;
    if (!top || scopeTotal <= 0) return null;
    const share = Math.min(100, Math.round((top.activity / scopeTotal) * 100));
    if (share >= 60) return `Concentrated. ${top.label} accounts for about ${share}% of these calls, likely from one machine's workflow.`;
    if (share >= 25) return `Led by ${top.label} (~${share}%), with ${Math.max(0, distinct - 1)} other endpoint${distinct === 2 ? "" : "s"} calling it.`;
    return `Diffuse. ${distinct} endpoints call this tool with no single driver, suggesting organization-wide use.`;
  }, [epData, scopeTotal]);

  const drillInto = (dim) => (id) =>
    ctx?.onNavigatePath?.([...scopePath, { dim, ids: [String(id)], id: String(id) }]);
  const onSelectTrace = (id) => { ctx?.traceLog?.onSelectTrace?.(id); slot?.onExpandDetail?.(); };
  const goToServer = () => {
    if (!server) return;
    ctx?.onNavigatePath?.([...scopePath.slice(0, -1), { dim: "mcp_servers", ids: [server], id: server }]);
  };

  function openDef(v) {
    const parts = [v.def.description || "No description declared for this tool."];
    if (v.def.schema) parts.push(`Input schema:\n${JSON.stringify(v.def.schema, null, 2)}`);
    ctx?.onOpenDetail?.({
      eyebrow: `${heroTitle} · definition`,
      title: versions.length > 1 ? `Definition v${v.n} · ${v.count} prompt${v.count === 1 ? "" : "s"}` : "Definition",
      body: parts.join("\n\n"),
      detailKey: `${toolName}:def:v${v.n}`,
    });
    slot?.onExpandDetail?.();
  }

  if (!toolName) {
    return (
      <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
        <PageHero eyebrow="Tool" title="No tool selected" />
      </ScrollArea>
    );
  }

  const driftNote = versions.length > 1 ? "definitions differ" : "stable definition";
  const status = defLoading
    ? "reading tool definition…"
    : found.length === 0
      ? "No captured definition for this tool in scope yet."
      : `${versions.length} definition variant${versions.length === 1 ? "" : "s"}${versions.length > 1 ? " (drift)" : ""}`;

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
      <PageHero
        eyebrow={server ? "MCP tool" : "Tool"}
        title={heroTitle}
        detail={server ? `Provided by ${labelForMcpServer(server)} · ${toolName}` : "Agent / built-in tool"}
        metrics={[
          { label: "Prompts", value: scopeTotal ? formatCompact(scopeTotal) : "–", note: "called this tool" },
          { label: "Endpoints", value: epData?.total ? formatCompact(epData.total) : "–", note: "calling this tool" },
          { label: "Agents", value: agData?.total ? formatCompact(agData.total) : "–", note: "using this tool" },
          { label: "Definition variants", value: versions.length ? String(versions.length) : "–", note: driftNote },
        ]}
      />
      <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[4px]">
        <span className={NOTE_CLASS}>{status}</span>
        {server ? (
          <button
            type="button"
            onClick={goToServer}
            className="text-[11px] leading-[1.1] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 transition-colors hover:text-[color:var(--ink)] cursor-pointer"
          >
            View {labelForMcpServer(server)} server →
          </button>
        ) : null}
      </div>

      {/* Definition + drift — what the tool declares, from the `tool` signal. */}
      <Sheet
        eyebrow="Definition"
        title="What this tool declares"
        detail="The tool's description and input schema as offered to the model, from the durable tool signal. More than one variant means the definition drifted across prompts (version skew or tampering)."
        tight
      >
        {versions.length > 1 ? (
          <div className="flex flex-wrap items-center gap-[4px] pb-[6px]">
            {versions.map((v) => (
              <FilterChip key={v.n} label={`v${v.n} · ${v.count}`} active={activeVersion?.n === v.n} onClick={() => setVersionSel(v.n)} />
            ))}
          </div>
        ) : null}
        {activeDef ? (
          <button
            type="button"
            onClick={() => openDef(activeVersion)}
            aria-label="Open full definition in the detail panel"
            className="block w-full max-h-[240px] overflow-auto whitespace-pre-wrap break-words border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] p-[8px] text-left text-[12px] leading-[1.5] text-[color:var(--ink)] cursor-pointer hover:border-[color:var(--line-strong)]"
          >
            {activeDef.description || "No description declared for this tool."}
            {activeDef.schema ? (
              <span className={`${NOTE_CLASS} mt-[6px] block`}>Select to view the input schema.</span>
            ) : null}
          </button>
        ) : (
          <span className={`${NOTE_CLASS} block py-[6px]`}>
            {defLoading ? "reading tool definition…" : "No captured definition for this tool in scope yet."}
          </span>
        )}
      </Sheet>

      {/* Composition — who calls this tool. */}
      <Sheet eyebrow="Composition" title="Who uses this tool" tight>
        {concentration ? (
          <span className="block pb-[6px] text-[12px] leading-[1.45] text-[color:var(--muted)]">{concentration}</span>
        ) : null}
        <div className="grid gap-x-[18px] gap-y-[14px] [grid-template-columns:repeat(3,minmax(0,1fr))]">
          <CompositionPanel title="Endpoints" data={epData} loading={epLoading} scopeTotal={scopeTotal} onDrill={drillInto("endpoints")} />
          <CompositionPanel title="Identities" data={idData} loading={idLoading} scopeTotal={scopeTotal} onDrill={drillInto("identities")} />
          <CompositionPanel title="Agents" data={agData} loading={agLoading} scopeTotal={scopeTotal} onDrill={drillInto("agents")} />
        </div>
      </Sheet>

      {/* Recent calls — prompts that called this tool, server-paginated. */}
      <TraceTable
        rows={traces}
        selectedTraceId={ctx?.traceLog?.selectedTraceId || ""}
        onSelectTrace={onSelectTrace}
        showEndpoint={ctx?.traceLog?.showEndpoint}
        loading={tracesLoading}
        emptyMessage="No calls to this tool in scope."
      />
    </ScrollArea>
  );
}
